import { useMemo } from "react";

import { useActivationContext, useAudienceContext } from "@/context";
import { getAudienceBreadcrumb, getActivationBreadcrumb } from "@/utils";
import { ActivePage, Breadcrumb } from "@/types";

const AUDIENCE_PAGES = ['Audiences', 'Upload', 'Mindsets', 'Sites', 'Audience Library'];

/**
 * Breadcrumbs for the current audience or activation page. Returns undefined
 * when disabled, so the page falls back to its default breadcrumbs.
 */
export const usePageBreadcrumbs = (
  activePage: ActivePage | null,
  pagePath: string,
  enabled: boolean
): Breadcrumb[] | undefined => {
  const { currentAudience } = useAudienceContext();
  const { currentActivation } = useActivationContext();

  return useMemo(() => {
    if (!enabled) return undefined;
    if (!activePage) return [];

    // Until the page's audience or activation is in the context, show the
    // default breadcrumbs rather than another one's name
    const id = pagePath.replace(activePage.path, '').split('/').filter(Boolean)[0];
    const isPending = (loadedId?: string) => !!id && id !== 'new' && loadedId !== id;

    return AUDIENCE_PAGES.includes(activePage.title)
      ? getAudienceBreadcrumb(activePage, pagePath, currentAudience, isPending(currentAudience?.audience_id))
      : getActivationBreadcrumb(activePage, pagePath, currentActivation, isPending(currentActivation?.campaign_id));

  }, [enabled, activePage, pagePath, currentAudience, currentActivation]);
};
